import type { FC } from 'react';
import type { Language } from '@/types/locales';
import { Card, Flex, Icon, Typography } from '@ui';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslation } from 'react-i18next';
import { CarouselButtons } from '@/components/CarouselButtons/CarouselButtons';
import { CarouselDots } from '@/components/CarouselDots/CarouselDots';
import { useUpcomingEvents } from '@/queries/useUpcomingEvents';
import { getImageLink } from '@/utils/getImageLink';
import styles from './EventsCarousel.module.css';

export const EventsCarousel: FC = () => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ slidesToScroll: 3 });
    const { data: events } = useUpcomingEvents();
    const { t, i18n } = useTranslation();

    if (!events.length) {
        return (
            <Flex vertical align="center" justify="center" gap={16} className={styles.emptyContainer}>
                <Icon.CalendarClock size={48} color="textTertiary" />
                <Typography.Paragraph level={2} color="textSecondary">{t('Events_Events_Empty')}</Typography.Paragraph>
            </Flex>
        );
    }

    return (
        <Flex vertical gap={48} className={styles.container}>
            <div className={styles.emblaWrapper} ref={emblaRef}>
                <div className={styles.emblaContainer}>
                    {events.map(({ id, image, name, description, start_utc, end_utc }) => (
                        <div className={styles.emblaSlide} key={id}>
                            <Card
                                imageSrc={getImageLink(image)}
                                title={name}
                                description={description?.[i18n.language as Language] || ''}
                                footer={{
                                    icon: <Icon.Clock size={10} />,
                                    text: `${dayjs.utc(start_utc).format('DD.MM.YYYY HH:mm')}–${dayjs.utc(end_utc).format('HH:mm')} UTC`
                                }}
                            />
                        </div>
                    ))}
                </div>
                <div className={styles.peekShadow} />
            </div>
            <Flex justify="space-between">
                <CarouselDots emblaApi={emblaApi} />
                <CarouselButtons emblaApi={emblaApi} />
            </Flex>
        </Flex>
    );
};
